import { View } from '@/components/ui/tw-wrappers';
import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';
import Animated, { FadeIn } from 'react-native-reanimated';

import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';

import { useBenchmarkingStore } from '@/stores/benchmarking-store';

import { Text } from '@/components/text';
import { Button } from '@/components/ui/button';

import { MultiCountrySelect } from '@/components/multi-country-select';
import { NumberInput } from '@/components/ui/number-input';
import { Select } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Colors } from '@/constants/theme';

import { SCENARIO_TYPES } from '@/constants/scenarios';
import { track } from '@/lib/firebase';
import { CommonFormValues, getCommonSchema } from '@/types/common.schema';
import { Country } from '@/types/country';

export function CommonSectionOnboarding({
  countries,
  countriesLoading,
  countriesError,
}: {
  countries: Country[];
  countriesLoading?: boolean;
  countriesError?: string | null;
}) {
  const { t } = useTranslation();
  // const isLoggedIn = useAuthStore(s => s.isLoggedIn);

  // const SCENARIO_OPTIONS = useMemo(() => {
  //   return SCENARIO_TYPES.filter(type => isLoggedIn || type !== 'alsoThere').map(type => ({
  //     id: type,
  //     label: t(`scenarios.types.${type}.title`),
  //   }));
  // }, [isLoggedIn, t]);

  const SCENARIO_OPTIONS = useMemo(() => {
    return SCENARIO_TYPES.filter(type => type !== 'alsoThere').map(type => ({
      id: type,
      label: t(`scenarios.types.${type}.title`),
    }));
  }, [t]);

  const commonProps = useBenchmarkingStore(s => s.commonProps);
  const setCommonProps = useBenchmarkingStore(s => s.setCommonProps);
  const addBenchmarking = useBenchmarkingStore(s => s.addBenchmarking);

  const schema = useMemo(() => getCommonSchema(t), [t]);
  // const { blockedSet } = useRestrictions();
  // const blockedIds = useMemo(() => Array.from(blockedSet), [blockedSet]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    clearErrors,
  } = useForm<CommonFormValues>({
    resolver: zodResolver(schema),
    defaultValues: commonProps as any,
    mode: 'onTouched',
  });

  const scenario = watch('scenario');

  const onRun = handleSubmit(values => {
    track('benchmarking_onboarding_completed', values);
    setCommonProps(values);
    addBenchmarking({});
  });

  return (
    <Animated.View entering={FadeIn} style={styles.container}>
      <View style={styles.content}>
        <View style={styles.mainBody} className='flex-col gap-10 lg:flex-row lg:gap-0'>
          {/* Left Column: Market Scope */}
          <View style={styles.columnPart} className='flex-none lg:flex-1'>
            {/* vendorIdList */}
            <View style={styles.fieldGroup}>
              {countriesError ? (
                <View style={styles.inlineError}>
                  <MaterialIcons name='error-outline' size={20} color={Colors.light.error} />
                  <Text style={styles.inlineErrorText}>{countriesError}</Text>
                </View>
              ) : null}
              <Controller
                control={control}
                name='vendorIdList'
                render={({ field: { value, onChange } }) => (
                  <MultiCountrySelect
                    id='vendorIdList'
                    name='vendorIdList'
                    label={t('onboarding.fields.vendorCountryList')}
                    tooltip={t('onboarding.fields.vendorCountryListTooltip')}
                    countries={countries}
                    valueIds={value || []}
                    disabled={countriesLoading}
                    // disabledIds={blockedIds}
                    onChange={ids => {
                      onChange(ids);
                      // if (ids.length > 0) clearErrors('vendorIdList');

                      // // Auto logic for the single selection
                      // if (ids.length === 1) {
                      //   setValue('customerId', ids[0], { shouldValidate: true });
                      // } else if (ids.length === 0) {
                      //   setValue('customerId', '', { shouldValidate: true });
                      // } else {
                      //   // If multiple, ensure current customerId is in the list
                      //   if (customerId && !ids.includes(customerId)) {
                      //     setValue('customerId', '', { shouldValidate: true });
                      //   }
                      // }
                    }}
                  />
                )}
              />
              {errors.vendorIdList ? <Text style={styles.errorText}>{errors.vendorIdList.message}</Text> : null}
            </View>
            {/* invoicing */}
            <View style={styles.fieldGroup}>
              <Text style={styles.fieldLabel}>
                {t('onboarding.fields.invoicingAmount', 'What is the estimated budget and in what currency?')}
              </Text>
              <View style={styles.budgetRow}>
                <Controller
                  control={control}
                  name='invoicingAmount'
                  render={({ field: { value, onChange } }) => (
                    <NumberInput
                      id='invoicingAmount'
                      name='invoicingAmount'
                      style={{ flex: 1 }}
                      value={value}
                      onChange={onChange}
                      prefix={'USD'}
                      placeholder='50.000'
                    />
                  )}
                />
              </View>
              {errors.invoicingAmount && <Text style={styles.errorText}>{errors.invoicingAmount.message}</Text>}
            </View>

            {/* Select Scenarios */}
            {/* <View style={styles.fieldGroup}>
              <Controller
                control={control}
                name='selectedScenarios'
                render={({ field: { value, onChange } }) => (
                  <MultiSelectChips
                    label={t('onboarding.fields.selectScenarios')}
                    tooltip={t('onboarding.fields.selectScenariosTooltip')}
                    options={SCENARIO_OPTIONS}
                    selectedIds={value || []}
                    onChange={onChange}
                  />
                )}
              />
            </View> */}
          </View>

          {/* Right Column: Financial Parameters */}
          <View style={styles.columnPart} className='flex-none lg:flex-1'>
            {/* Scenario */}
            <View style={styles.fieldGroup}>
              <Controller
                control={control}
                name='scenario'
                render={({ field: { value, onChange } }) => (
                  <Select
                    id='scenario'
                    name='scenario'
                    label={t('onboarding.fields.scenario')}
                    tooltip={t('onboarding.fields.scenarioTooltip')}
                    value={value}
                    onValueChange={onChange}
                    options={SCENARIO_OPTIONS.map(s => ({
                      value: s.id,
                      label: s.label,
                    }))}
                    placeholder={t('common.select', 'Select...')}
                  />
                )}
              />
              {errors.scenario ? <Text style={styles.errorText}>{errors.scenario.message}</Text> : null}
            </View>
            {scenario === 'localPartner' && (
              <View style={styles.fieldGroup}>
                <Text style={styles.fieldLabel}>
                  {t('onboarding.fields.partnerMargin', 'What is the Partner Margin?')}
                </Text>
                <Controller
                  control={control}
                  name='partnerMargin'
                  render={({ field: { value, onChange } }) => (
                    <View style={styles.sliderRow}>
                      <View style={styles.valueBox}>
                        <Text style={styles.valueText}>{value}%</Text>
                      </View>
                      <View style={{ flex: 1 }}>
                        <Slider value={value} onChange={onChange} />
                      </View>
                    </View>
                  )}
                />
              </View>
            )}
          </View>
        </View>
        <View style={styles.footer}>
          {/* <View style={styles.infoHint}>
            <MaterialIcons name='info-outline' size={24} color='#0B5FFF' />
            <Text style={styles.infoHintText}>
              {t('onboarding.commonSectionHint')}
            </Text>
          </View> */}
          <View style={styles.footerAction}>
            <Button
              label={t('onboarding.actions.continue', 'Continue to the simulation')}
              onPress={onRun}
              style={styles.runButton}
            />
          </View>
        </View>
      </View>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    maxWidth: 1100,
    paddingHorizontal: 20,
    alignSelf: 'center',
    marginBottom: 40,
  },
  content: {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderRadius: 40,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.05)',
    shadowColor: Colors.light.shadow,
    shadowOffset: { width: 4, height: 4 },
    shadowOpacity: 1,
    shadowRadius: 0,
    elevation: 2,
    overflow: 'hidden',
  },
  header: {
    justifyContent: 'center',
    paddingHorizontal: 30,
    backgroundColor: '#eaeff4',
    paddingVertical: 30,
  },
  mainTitle: {
    fontSize: 28,
  },
  mainBody: {
    paddingHorizontal: 30,
    paddingVertical: 30,
  },
  columnPart: {
    paddingHorizontal: 20,
    gap: 24,
  },

  fieldGroup: {
    gap: 10,
  },
  fieldLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0B5FFF',
  },
  errorText: {
    color: Colors.light.error,
    fontSize: 12,
    marginTop: -4,
  },
  inlineError: {
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    padding: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(239, 68, 68, 0.2)',
    marginBottom: 20,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    justifyContent: 'center',
  },
  inlineErrorText: {
    color: Colors.light.error,
    fontSize: 14,
    textAlign: 'center',
    fontWeight: '500',
  },
  sliderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 20,
  },
  valueBox: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#898b8c',
    minWidth: 70,
    alignItems: 'center',
  },
  valueText: {
    fontSize: 14,
    fontWeight: '600',
  },
  budgetRow: {
    flexDirection: 'row',
    gap: 12,
    alignItems: 'center',
    width: '100%',
    flexWrap: 'wrap',
  },
  currencySelectWrap: {
    width: 100,
  },
  footer: {
    paddingHorizontal: 30,
    paddingBottom: 30,
    gap: 16,
  },
  infoHint: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 18,
    backgroundColor: 'rgba(11, 95, 255, 0.06)',
    borderWidth: 1,
    borderColor: 'rgba(11, 95, 255, 0.15)',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  infoHintText: {
    flex: 1,
    fontSize: 16,
    lineHeight: 24,
    color: '#1a2a4a',
  },
  footerAction: {
    justifyContent: 'center',
    alignItems: 'flex-end',
    marginTop: 24,
  },
  runButton: {
    paddingHorizontal: 32,
    height: 44,
    borderRadius: 12,
  },
});
